/**
 * Lead List View Queries
 *
 * React Query hooks for displaying lead data in list views.
 * Uses centralized mock data with localStorage persistence.
 *
 * TO REPLACE WITH REAL API:
 * 1. Keep query function signatures the same
 * 2. Replace queryFn with API calls (see comments in each function)
 * 3. Remove mock delays
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { persistedLeadListViews, persistedLeadRecords } from '../mockData/leads';
import { getRecordsForObjectPage } from '../recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { USE_RDS_FOR_CORE_OBJECTS, isRdsCoreObject } from '../../config/rdsConfig';
import { normalizeRecordForListView } from '../../utils/listViewRecordNormalizer';
import type { ListView, ListViewRecord, ListViewData } from '../../types/listView';

// Mock list views for Leads (kept for reference, use persisted versions in queries)
const mockLeadListViews: ListView[] = [
  {
    id: 'lv_leads_all',
    name: 'All Leads',
    apiName: 'all_leads',
    objectApiName: 'Lead',
    description: 'All lead records',
    isDefault: true,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Lead Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'company', fieldLabel: 'Company', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'rating', fieldLabel: 'Rating', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'leadSource', fieldLabel: 'Lead Source', fieldType: 'text', isVisible: true, order: 4 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 5 },
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_leads_hot',
    name: 'Hot Leads',
    apiName: 'hot_leads',
    objectApiName: 'Lead',
    description: 'Hot-rated leads',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Lead Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'company', fieldLabel: 'Company', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 4 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 5 },
    ],
    filters: [
      { fieldApiName: 'rating', operator: 'equals', value: 'Hot' }
    ],
    createdBy: 'Admin',
    createdDate: '2024-02-15T10:30:00Z'
  },
  {
    id: 'lv_leads_new',
    name: 'New Leads',
    apiName: 'new_leads',
    objectApiName: 'Lead',
    description: 'Leads with New status',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Lead Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'company', fieldLabel: 'Company', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'rating', fieldLabel: 'Rating', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'leadSource', fieldLabel: 'Lead Source', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'date', isVisible: true, order: 4 },
    ],
    filters: [
      { fieldApiName: 'status', operator: 'equals', value: 'New' }
    ],
    sortBy: {
      fieldApiName: 'createdDate',
      direction: 'desc'
    },
    createdBy: 'Admin',
    createdDate: '2024-03-10T14:00:00Z'
  },
  {
    id: 'lv_leads_qualified',
    name: 'Qualified Leads',
    apiName: 'qualified_leads',
    objectApiName: 'Lead',
    description: 'Qualified leads ready for conversion',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Lead Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'company', fieldLabel: 'Company', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'rating', fieldLabel: 'Rating', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 3 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 4 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 5 },
    ],
    filters: [
      { fieldApiName: 'status', operator: 'equals', value: 'Qualified' }
    ],
    createdBy: 'Admin',
    createdDate: '2024-04-01T10:00:00Z'
  }
];

// Mock lead records
const mockLeadRecords: ListViewRecord[] = [
  {
    id: 'lead_001',
    name: 'Sarah Martinez',
    company: 'Tech Innovations Inc',
    title: 'VP of Engineering',
    email: 'sarah.martinez@techinnovations.com',
    phone: '(555) 789-0123',
    status: 'Qualified',
    leadSource: 'Website',
    rating: 'Hot',
    owner: 'John Smith',
    industry: 'Technology',
    createdDate: '2025-01-10T09:00:00Z',
    lastContact: '2025-01-15T14:30:00Z'
  },
  {
    id: 'lead_002',
    name: 'Michael Chen',
    company: 'Global Manufacturing Corp',
    title: 'Director of Operations',
    email: 'mchen@globalmanuf.com',
    phone: '(555) 890-1234',
    status: 'Contacted',
    leadSource: 'Referral',
    rating: 'Warm',
    owner: 'Sarah Johnson',
    industry: 'Manufacturing',
    createdDate: '2025-01-12T10:30:00Z',
    lastContact: '2025-01-14T11:00:00Z'
  },
  {
    id: 'lead_003',
    name: 'Emily Rodriguez',
    company: 'Retail Solutions LLC',
    title: 'CEO',
    email: 'emily@retailsolutions.com',
    phone: '(555) 901-2345',
    status: 'New',
    leadSource: 'Trade Show',
    rating: 'Hot',
    owner: 'Michael Chen',
    industry: 'Retail',
    createdDate: '2025-01-15T14:00:00Z'
  },
  {
    id: 'lead_004',
    name: 'David Park',
    company: 'Financial Services Group',
    title: 'Managing Director',
    email: 'dpark@finservices.com',
    phone: '(555) 012-3456',
    status: 'Contacted',
    leadSource: 'Cold Call',
    rating: 'Warm',
    owner: 'Emily Davis',
    industry: 'Finance',
    createdDate: '2025-01-08T09:15:00Z',
    lastContact: '2025-01-13T16:45:00Z'
  },
  {
    id: 'lead_005',
    name: 'Jennifer Taylor',
    company: 'Healthcare Innovations',
    title: 'CTO',
    email: 'jtaylor@healthcareinnovations.com',
    phone: '(555) 123-4567',
    status: 'Qualified',
    leadSource: 'Partner',
    rating: 'Hot',
    owner: 'David Wilson',
    industry: 'Healthcare',
    createdDate: '2025-01-05T11:30:00Z',
    lastContact: '2025-01-14T10:15:00Z'
  },
  {
    id: 'lead_006',
    name: 'Robert Anderson',
    company: 'Construction Pro LLC',
    title: 'Owner',
    email: 'randerson@constructionpro.com',
    phone: '(555) 234-5678',
    status: 'Unqualified',
    leadSource: 'Website',
    rating: 'Cold',
    owner: 'Sarah Johnson',
    industry: 'Construction',
    createdDate: '2024-12-28T13:00:00Z',
    lastContact: '2025-01-05T09:30:00Z'
  },
  {
    id: 'lead_007',
    name: 'Lisa Wong',
    company: 'Education Tech Solutions',
    title: 'Founder',
    email: 'lwong@edtechsolutions.com',
    phone: '(555) 345-6789',
    status: 'New',
    leadSource: 'Referral',
    rating: 'Warm',
    owner: 'John Smith',
    industry: 'Education',
    createdDate: '2025-01-14T15:45:00Z'
  },
  {
    id: 'lead_008',
    name: 'James Miller',
    company: 'Logistics Central',
    title: 'VP of Sales',
    email: 'jmiller@logisticscentral.com',
    phone: '(555) 456-7890',
    status: 'Contacted',
    leadSource: 'Trade Show',
    rating: 'Warm',
    owner: 'Michael Chen',
    industry: 'Logistics',
    createdDate: '2025-01-11T08:30:00Z',
    lastContact: '2025-01-15T14:00:00Z'
  },
  {
    id: 'lead_009',
    name: 'Amanda Foster',
    company: 'Marketing Pros',
    title: 'Director of Marketing',
    email: 'afoster@marketingpros.com',
    phone: '(555) 567-8901',
    status: 'New',
    leadSource: 'Website',
    rating: 'Hot',
    owner: 'John Smith',
    industry: 'Marketing',
    createdDate: '2025-01-16T09:00:00Z'
  },
  {
    id: 'lead_010',
    name: 'Christopher Lee',
    company: 'Software Dynamics',
    title: 'CIO',
    email: 'clee@softwaredynamics.com',
    phone: '(555) 678-9012',
    status: 'Qualified',
    leadSource: 'Referral',
    rating: 'Hot',
    owner: 'Emily Davis',
    industry: 'Technology',
    createdDate: '2025-01-13T11:00:00Z',
    lastContact: '2025-01-16T10:00:00Z'
  }
];

/**
 * Get available list views for leads
 */
export function useLeadListViews() {
  return useQuery({
    queryKey: ['leadListViews'],
    queryFn: async () => {
      // MOCK: Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));

      // MOCK: Get from localStorage via mockDataManager
      return persistedLeadListViews.get();

      // REAL API (when ready):
      // const response = await fetch('/api/list-views?object=Lead');
      // return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

const DEFAULT_PAGE_SIZE = 25;

/**
 * Get list view data (view configuration + records) with server-side pagination
 */
export function useLeadListViewData(
  listViewId: string,
  options?: { page?: number; pageSize?: number }
) {
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
  const useBackend = shouldUseGobodo() || (USE_RDS_FOR_CORE_OBJECTS && isRdsCoreObject('Lead'));

  return useQuery<ListViewData>({
    queryKey: ['leadListViewData', listViewId, page, pageSize],
    queryFn: async () => {
      const allViews = persistedLeadListViews.get();
      const listView = allViews.find(lv => lv.id === listViewId);
      if (!listView) throw new Error('List view not found');

      if (useBackend) {
        const { records: pageRecords, totalRecords } = await getRecordsForObjectPage('Lead', { page, size: pageSize });
        const seen = new Set<string>();
        let records: ListViewRecord[] = pageRecords
          .map(r => normalizeRecordForListView('Lead', r) as ListViewRecord)
          .filter(r => {
            if (seen.has(r.id)) return false;
            seen.add(r.id);
            return true;
          });

        if (listView.filters?.length) {
          listView.filters.forEach(filter => {
            records = records.filter(record => {
              const fieldValue = record[filter.fieldApiName];
              switch (filter.operator) {
                case 'equals': return fieldValue === filter.value;
                case 'notEquals': return fieldValue !== filter.value;
                case 'contains': return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
                case 'startsWith': return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
                default: return true;
              }
            });
          });
        }

        if (listView.sortBy) {
          records = [...records].sort((a, b) => {
            const aValue = a[listView.sortBy!.fieldApiName];
            const bValue = b[listView.sortBy!.fieldApiName];
            if (listView.sortBy!.direction === 'asc') return aValue > bValue ? 1 : -1;
            return aValue < bValue ? 1 : -1;
          });
        }

        return { listView, records, totalRecords };
      }

      const allRecords = persistedLeadRecords.get();
      let filtered: ListViewRecord[] = [...allRecords];
      if (listView.filters?.length) {
        listView.filters.forEach(filter => {
          filtered = filtered.filter(record => {
            const fieldValue = record[filter.fieldApiName];
            switch (filter.operator) {
              case 'equals': return fieldValue === filter.value;
              case 'notEquals': return fieldValue !== filter.value;
              case 'contains': return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
              case 'startsWith': return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
              default: return true;
            }
          });
        });
      }
      if (listView.sortBy) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = a[listView.sortBy!.fieldApiName];
          const bValue = b[listView.sortBy!.fieldApiName];
          if (listView.sortBy!.direction === 'asc') return aValue > bValue ? 1 : -1;
          return aValue < bValue ? 1 : -1;
        });
      }
      const totalRecords = filtered.length;
      const start = page * pageSize;
      const records = filtered.slice(start, start + pageSize);
      return { listView, records, totalRecords };
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!listViewId,
    placeholderData: keepPreviousData,
  });
}

/**
 * Get default list view for leads
 */
export function useDefaultLeadListView() {
  return useQuery({
    queryKey: ['defaultLeadListView'],
    queryFn: async () => {
      // MOCK: Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 200));

      // MOCK: Get from localStorage
      const allViews = persistedLeadListViews.get();
      const defaultView = allViews.find(lv => lv.isDefault);

      return defaultView || allViews[0];

      // REAL API (when ready):
      // const response = await fetch('/api/list-views?object=Lead&default=true');
      // return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Update a lead list view
 * Used by EditViewModal to persist changes
 */
export function useUpdateLeadListView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ListView> }) => {
      // MOCK: Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // MOCK: Update in localStorage via mockDataManager
      const updated = persistedLeadListViews.update(id, {
        ...updates,
        lastModifiedDate: new Date().toISOString()
      });

      if (!updated) {
        throw new Error('List view not found');
      }

      return updated;

      // REAL API (when ready):
      // const response = await fetch(`/api/list-views/${id}`, {
      //   method: 'PATCH',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(updates)
      // });
      // if (!response.ok) throw new Error('Failed to update list view');
      // return response.json();
    },
    onSuccess: (data) => {
      // Invalidate queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['leadListViews'] });
      queryClient.invalidateQueries({ queryKey: ['leadListViewData', data.id] });
      queryClient.invalidateQueries({ queryKey: ['defaultLeadListView'] });
    },
  });
}
